/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useRef } from 'react';

import Image from 'next/image';
import { screensMap } from '..';
import { getPlanStatus } from '../EmiPlans/helper';
import EMandatePollingPopup from '../EMandateSuccess/EmandatePollingScreen';
import { useGlobalStore } from '@/store';
import SuccessScreen from '@/shared/Components/SuccessScreen';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import EMandatePopUp from '@/views/DigiLockerSuccess/EMandatePopUp';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { PopupFailureProps } from '@/shared/types';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { EmiPlans, LENDER_NAMES } from '@/api/types';
import Chip from '@/lib/Chip';
import { LENDER_MAPPING } from '@/constants';

const DigiLockerSuccess: React.FC = () => {
  const [showSuccess, setShowSuccess] = useState(true);
  const [showEmandateSuccess, setShowEmandateSuccess] = useState(false);
  const [openEmandate, setOpenEmandate] = useState(false);
  const eMandateUrlRef = useRef<string | null>(null);
  const [didEmandateFail, setDidEmandateFail] = useState(false);
  const setActiveView = useGlobalStore((store) => store.setActiveView);
  const [isEmandatePopUpVisible, setIsEmandatePopUpVisible] = useState(false);
  const approvedCreditLimit = useGlobalStore((store) => store.approvedCreditLimit);
  const [emandateError, setEmandateError] = useState('');
  const { user, lenderDetails, selectedTenure, userAccessToken, setRepaymentId } = useGlobalStore();

  const [state, setState] = useState({
    showEmandateLoader: false,
  });

  const handleContinue = (): void => {
    setIsEmandatePopUpVisible(true);
    setShowSuccess(false);
    // Continue to Emandate clicked
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      screen: screensMap.DIGILOCKER_SUCCESS,
      objectName: EVENT_NAMES.CONTINUE_TO_EMANDATE,
      properties: {
        contact: user.contact,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  };

  const redirectToEmandate = async (url: string): Promise<void> => {
    eMandateUrlRef.current = url;
    setOpenEmandate(true);
    setIsEmandatePopUpVisible(false);
  };

  const handleEmandateSuccess = (data: Record<string, boolean | string>): void => {
    if (data.success) {
      // Wait for 1 second before starting the polling
      setIsEmandatePopUpVisible(false);
      setOpenEmandate(false);
      if (data.razorpay_payment_id) {
        setRepaymentId(data.razorpay_payment_id as string);
      }
      setState({
        showEmandateLoader: true,
      });
    } else {
      setDidEmandateFail(true);
    }

    // Track Emandate Callback Success
    trackHelper({
      actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
      objectName: EVENT_NAMES.EMANDATE_CALLBACK_SUCCESS,
      screen: screensMap.DIGILOCKER_SUCCESS,
      properties: {
        contact: user.contact,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        data,
      },
    });
  };

  const handleEmandateFailure = (e: PopupFailureProps): void => {
    setDidEmandateFail(true);
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setEmandateError((e.message as unknown as any)?.description || '');
    }
    setOpenEmandate(false);

    // Track Emandate Failure
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.EMANDATE_POPUP_FAILURE,
      screen: screensMap.DIGILOCKER_SUCCESS,
      properties: {
        contact: user.contact,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  };

  const handleEmandatePollSuccess = (): void => {
    // setShowEmandateSuccess(true);
    setActiveView('E_MANDATE_SUCCESS');
  };

  return (
    <>
      <div className="d:h-full d:flex d:justify-center d:items-center">
        <SuccessScreen
          title="KYC successful! 🎉"
          topDescription={`You're eligible for a credit limit of ₹${
            (approvedCreditLimit as number) / 100
          }`}
          ctaText="Continue"
          onCtaClick={handleContinue}
          isFullScreen={true}
        >
          <Chip className="[&&]:rounded-3xl">
            {selectedTenure ? (
              <>
                <div className="border-surface-neutral-faded border w-fit p-1 bg-white-normal rounded-full border-opacity-18 mr-2">
                  <Image
                    width={15}
                    height={15}
                    alt={selectedTenure.provider}
                    src={`/${selectedTenure.provider}.svg`}
                  />
                </div>
                <p className="text-xs font-medium">
                  Powered by {LENDER_MAPPING[selectedTenure.provider as LENDER_NAMES]}
                </p>
              </>
            ) : null}
          </Chip>
        </SuccessScreen>
      </div>
      {isEmandatePopUpVisible ? (
        <EMandatePopUp
          onDismiss={(): void => setIsEmandatePopUpVisible(false)}
          cb={redirectToEmandate}
        />
      ) : null}
      {state.showEmandateLoader ? (
        <EMandatePollingPopup
          onSuccess={handleEmandatePollSuccess}
          onRetry={(): void => {
            setIsEmandatePopUpVisible(true);
          }}
          isOpen={state.showEmandateLoader}
          onDismiss={(): void => {
            setState({
              showEmandateLoader: false,
            });
          }}
        />
      ) : null}
      {openEmandate ? (
        <OpenPopupLoader
          serviceType="emandate"
          onFail={handleEmandateFailure}
          serviceName="Razorpay"
          url={eMandateUrlRef.current as string}
          progressText="e-Mandate is in progress on"
          onSuccess={handleEmandateSuccess}
        />
      ) : null}

      {didEmandateFail ? (
        <PaymentCancelWrapper
          onDismiss={(): null => null}
          isOpen={didEmandateFail}
          ctaText="Change payment method"
          title="E-mandate Failed"
          isCloseable={false}
          description={emandateError || 'E-Mandate failed. Try with a different payment method'}
        />
      ) : null}
    </>
  );
};

export default DigiLockerSuccess;
